// Lo que no llegó a Santi (store `fallos`): un «Hecho» o un «Cancelar» que el centro no aceptó o que no se supo si
// llegó, aunque se enviara con la ventana ya cerrada. El aviso se queda arriba de la ventana (y la pared lo dice) hasta
// que «Volver a probar» sale bien (con la MISMA clave si no se supo qué pasó; otra si el centro dijo que no), Pau lo
// quita o el contrato deja de estar abierto. «No se ha podido cancelar «X». Vuelve a probar.»
// Una respuesta del centro de antes que no se arregla volviendo a probar (NO_ES_TUYO: lo pidió Santi y aún cierra solo
// quien lo pidió; TABLERO_SIN_CANCELAR: aún no sabe cancelar) no es un fallo: el aviso la explica en gris, con el título
// y sin «Volver a probar» (daría lo mismo), y solo se quita con ✕.
import { Ban, Check, Info, Loader2, X } from 'lucide-react';
import { t } from '../../../i18n';
import { BotonIcono } from '../ui';
import { useHablaComoPau, usePelotasStore } from './store';
import type { FalloActo, RespuestaExplicada } from './store';

/** «No se ha podido cancelar «X». Vuelve a probar.» / «No se ha podido marcar como hecho «X». Vuelve a probar.» */
export const textoFallo = (f: Pick<FalloActo, 'motivo' | 'titulo'>): string =>
  t(f.motivo === 'cancelado' ? 'oficinaV2.pelotas.acto.falloCancelar' : 'oficinaV2.pelotas.acto.falloHecho', { titulo: f.titulo });

export function AvisosActo() {
  const fallos = usePelotasStore((s) => s.fallos);
  const comoPau = useHablaComoPau();
  if (!fallos.length || !comoPau) return null;
  return (
    <div className="col-span-full border-b border-oficina-borde bg-oficina-panel px-4 py-2" role="alert" data-testid="avisos-acto">
      <ul className="space-y-1">
        {fallos.map((f) => (f.explicado ? <AvisoExplicado key={f.n} f={f} explicado={f.explicado} /> : <AvisoReintentar key={f.n} f={f} />))}
      </ul>
    </div>
  );
}

/** Algo que no llegó (o no se sabe): en rojo, con «Volver a probar». */
function AvisoReintentar({ f }: { f: FalloActo }) {
  const reintentar = usePelotasStore((s) => s.reintentarFallo);
  const quitar = usePelotasStore((s) => s.quitarFallo);
  return (
    <li className="flex items-center gap-3 rounded-[10px] bg-oficina-error/10 py-1 pl-3 pr-1 ring-1 ring-inset ring-oficina-error/30" data-testid="aviso-fallo" data-tipo="reintentar" data-tema={f.temaId}>
      {f.motivo === 'cancelado' ? <Ban className="h-[18px] w-[18px] shrink-0 text-oficina-error" aria-hidden /> : <Check className="h-[18px] w-[18px] shrink-0 text-oficina-error" aria-hidden />}
      <p className="min-w-0 flex-1 text-[15px] leading-[22px] text-oficina-texto">{textoFallo(f)}</p>
      <button
        type="button"
        onClick={() => void reintentar(f.n)}
        disabled={f.reintentando}
        className="inline-flex h-11 shrink-0 items-center gap-2 rounded-lg px-3 text-[16px] font-semibold text-oficina-texto outline-none transition-colors hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-white/85 disabled:opacity-70"
        data-testid="aviso-fallo-reintentar"
      >
        {f.reintentando ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
        {t('oficinaV2.pelotas.acto.volverAProbar')}
      </button>
      <BotonIcono icono={X} etiqueta={t('oficinaV2.pelotas.acto.quitarAviso')} onClick={() => quitar(f.n)} className="h-11 w-11" data-testid="aviso-fallo-quitar" />
    </li>
  );
}

/** Una respuesta del centro de antes (`explicado`): se explica en gris; volver a probar daría lo mismo. */
function AvisoExplicado({ f, explicado }: { f: FalloActo; explicado: RespuestaExplicada }) {
  const quitar = usePelotasStore((s) => s.quitarFallo);
  return (
    <li className="flex items-center gap-3 rounded-[10px] bg-white/[0.04] py-1 pl-3 pr-1 ring-1 ring-inset ring-white/10" data-testid="aviso-fallo" data-tipo="explicado" data-codigo={explicado} data-tema={f.temaId}>
      <Info className="h-[18px] w-[18px] shrink-0 text-oficina-texto2" aria-hidden />
      <p className="min-w-0 flex-1 py-2.5 text-[15px] leading-[22px] text-oficina-texto2">
        <b className="font-semibold text-oficina-texto">{t('oficinaV2.pelotas.acto.entreComillas', { titulo: f.titulo })}</b> · {t(`oficinaV2.errores.pelotas.${explicado}`)}
      </p>
      <BotonIcono icono={X} etiqueta={t('oficinaV2.pelotas.acto.quitarAviso')} onClick={() => quitar(f.n)} className="h-11 w-11" data-testid="aviso-fallo-quitar" />
    </li>
  );
}
